import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/components";
import { cn } from "@/shared/utils";
import type { ReactNode } from "react";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

export type SelectOption<T = string> = {
    label: string;
    value: T;
};

interface SelectFormFieldProps<T extends FieldValues> {
    form: UseFormReturn<T>;
    label?: string;
    name: FieldPath<T>;
    options: SelectOption[];
    placeholder?: string;
    className?: string;
    labelClassName?: string;
    readOnly?: boolean;
    onValueChange?: (value: string) => void;
    /** Layout style: "stacked" (default) or "inline" for label and select on same line */
    layout?: "stacked" | "inline";
    /** Additional content to render after the select (e.g., "(aangepast)") */
    suffix?: ReactNode;
    optional?: boolean;
}

export function SelectFormField<T extends FieldValues>({
    form,
    label,
    name,
    options,
    placeholder,
    className,
    labelClassName,
    readOnly,
    onValueChange,
    layout = "stacked",
    suffix,
    optional,
}: Readonly<SelectFormFieldProps<T>>) {
    const isInline = layout === "inline";

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => {
                const handleValueChange = (value: string) => {
                    field.onChange(value);
                    onValueChange?.(value);
                };

                return (
                    <FormItem className={cn("relative", isInline && "flex items-center gap-2", className)}>
                        {label && (
                            <FormLabel optional={optional} className={labelClassName}>
                                {label}
                            </FormLabel>
                        )}
                        {readOnly ? (
                            <span className="w-full">{options.find((option) => option.value === field.value)?.label || "Onbekend"}</span>
                        ) : (
                            <Select onValueChange={handleValueChange} {...field}>
                                <FormControl>
                                    <SelectTrigger className={cn(!isInline && "w-full")}>
                                        <SelectValue placeholder={placeholder} />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {options.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {suffix}
                        <FormMessage />
                    </FormItem>
                );
            }}
        />
    );
}
